import React, { useState } from 'react';
import {
  ArrowSquareOut,
  CaretRight,
  ClipboardText,
  GitPullRequest,
  Prohibit,
  Warning,
} from '@phosphor-icons/react';
import { Flex, Text } from '@chatsdet/ui';
import { hasPlanContent } from '../../../features/exploratory-tests';
import type {
  ExploratoryUntestableItem,
  ExploratoryUntestableReason,
} from '../../../features/exploratory-tests/types';
import { safeHref } from '../../../utils/safeHref';
import { formatTicketingProviderLinkLabel } from '../../../utils/ticketingProviders';
import type { PlanPriority } from '../ExploratoryTestVerdict';
import type { ExploratoryTestData } from './types';

const PRIORITY_LABEL: Record<PlanPriority, string> = {
  p0: 'P0',
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
};

const PRIORITY_CHIP_CLASS: Record<PlanPriority, string> = {
  p0: 'bg-foreground text-background',
  p1: 'bg-muted text-foreground',
  p2: 'bg-muted/60 text-muted-foreground',
  p3: 'bg-muted/40 text-muted-foreground',
};

export function PriorityChip({ priority }: { priority: PlanPriority }) {
  return (
    <span
      className={`rounded px-1 py-0.5 text-[9px] font-semibold leading-none shrink-0 ${PRIORITY_CHIP_CLASS[priority]}`}
    >
      {PRIORITY_LABEL[priority]}
    </span>
  );
}

function PromptBlock({ instructions }: { instructions: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = React.useRef<HTMLParagraphElement>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    // +1 absorbs sub-pixel rounding so a non-clamped prompt does not get a toggle.
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [instructions, expanded]);

  return (
    <div className="px-3">
      <Flex align="center" gap="sm" className="mb-1.5">
        <ClipboardText size={14} weight="duotone" className="text-muted-foreground" />
        <Text weight="medium" size="xs" className="uppercase tracking-wide text-muted-foreground">
          Prompt
        </Text>
      </Flex>
      <p
        ref={ref}
        className={`text-sm text-foreground leading-relaxed whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}
      >
        {instructions}
      </p>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-primary hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export function PlanPanel({ test }: { test: ExploratoryTestData }) {
  const ticketHref = safeHref(test.externalTicketUrl);
  const hasPlan = hasPlanContent(test);

  return (
    <div className="h-full overflow-y-auto py-3 space-y-4">
      {test.instructions && <PromptBlock instructions={test.instructions} />}

      {ticketHref && test.externalTicketProvider && (
        <div className="px-3">
          <a
            href={ticketHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            {test.externalTicketProvider === 'github' ? (
              <GitPullRequest size={15} weight="bold" className="shrink-0" />
            ) : (
              <ArrowSquareOut size={15} weight="bold" className="shrink-0" />
            )}
            {formatTicketingProviderLinkLabel(
              test.externalTicketProvider,
              test.externalTicketIdentifier ?? null
            )}
          </a>
        </div>
      )}

      {test.plannedSummary && (
        <div className="px-3">
          <Text size="sm" className="text-foreground leading-relaxed">
            {test.plannedSummary}
          </Text>
        </div>
      )}

      {test.coverageWarning && (
        <div className="mx-3 px-3 py-2 border border-yellow-300 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 rounded">
          <Flex align="start" gap="sm">
            <Warning
              size={14}
              weight="fill"
              className="text-yellow-700 dark:text-yellow-400 mt-0.5 shrink-0"
            />
            <div>
              <Text
                weight="medium"
                size="xs"
                className="uppercase tracking-wide text-yellow-800 dark:text-yellow-300"
              >
                Plan coverage warning
              </Text>
              <Text size="sm" className="text-yellow-900 dark:text-yellow-200">
                {test.coverageWarning}
              </Text>
            </div>
          </Flex>
        </div>
      )}

      {test.plannedDataNotes && (
        <details className="group px-3">
          <summary className="flex cursor-pointer list-none items-center gap-1.5">
            <CaretRight
              size={12}
              weight="bold"
              className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
            />
            <ClipboardText size={14} weight="duotone" className="text-muted-foreground" />
            <Text
              weight="medium"
              size="xs"
              className="uppercase tracking-wide text-muted-foreground"
            >
              Plan & data staging notes
            </Text>
          </summary>
          <Text
            size="sm"
            className="mt-1.5 pl-[26px] text-foreground leading-relaxed whitespace-pre-wrap"
          >
            {test.plannedDataNotes}
          </Text>
        </details>
      )}

      <UntestableSection items={test.plannedUntestable ?? []} />

      {!hasPlan && (
        <div className="px-3">
          <Text size="sm" className="text-muted-foreground">
            No upfront plan for this run — the tester explores from the prompt and builds its
            checklist as it goes. See the Report tab for what it covered.
          </Text>
        </div>
      )}
    </div>
  );
}

const UNTESTABLE_REASON_LABEL: Record<ExploratoryUntestableReason, string> = {
  external_integration: 'External integration',
  gated_not_enableable: 'Feature gated off',
  needs_real_environment: 'Needs a real environment',
  manual_only: 'Manual verification only',
  other: 'Other',
};

/**
 * Planner-asserted "couldn't test in this sandbox" delta, grouped by reason.
 *
 * @internal exported for unit tests; PlanPanel is the only production caller.
 */
export function UntestableSection({ items }: { items: ReadonlyArray<ExploratoryUntestableItem> }) {
  if (items.length === 0) return null;

  const byReason = new Map<ExploratoryUntestableReason, ExploratoryUntestableItem[]>();
  for (const item of items) {
    const bucket = byReason.get(item.reason);
    if (bucket) bucket.push(item);
    else byReason.set(item.reason, [item]);
  }

  return (
    <div>
      <Flex align="center" gap="sm" className="mb-1.5 px-3">
        <Prohibit size={14} weight="duotone" className="text-muted-foreground" />
        <Text weight="medium" size="xs" className="uppercase tracking-wide text-muted-foreground">
          Couldn't test in sandbox ({items.length})
        </Text>
      </Flex>
      <div className="divide-y divide-border/60 border-y border-border/60">
        {[...byReason.entries()].map(([reason, group]) => (
          <div key={reason} className="px-3 py-2 space-y-1.5">
            <Text size="xs" weight="medium" className="text-muted-foreground">
              {UNTESTABLE_REASON_LABEL[reason] ?? UNTESTABLE_REASON_LABEL.other}
            </Text>
            {group.map((item, i) => (
              <div key={i}>
                <Text size="xs" className="text-foreground">
                  {item.name}
                </Text>
                <Text size="xs" className="text-muted-foreground mt-0.5 leading-relaxed">
                  How to test: {item.howToTest}
                </Text>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
